import React from 'react';
import glamorous from 'glamorous';
import {BLACK, CHARCOAL, DODGER_BLUE, FOCUS_BLUE, WHITE} from '../../../shared/style/colors';
import {BASE_TEXT, FONTS, WEIGHT} from '../../../shared/style/typography';
import {LAPTOP, PHONE, TABLET, DESKTOP} from '../../../shared/style/breakpoints';
import RequestDemo from '../../enterprise/components/request-demo';
import GhostButton from '../../../shared/library/buttons/base/ghost';
import SimpleButton from '../../../shared/library/buttons/base/simple';
import {ALPHA} from '../../../shared/style/color-utils';
import {MAX_PAGE_WIDTH} from '../../../shared/style/dimensions';

const RequestDemoExtraStyle = {
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  fontWeight: WEIGHT.BOLD,
  fontSize: 23,
  lineHeight: '68px',
  color: WHITE,
  background: FOCUS_BLUE,
  borderRadius: 4,
  zIndex: 1,
  padding: '13px 21px 15px 20px',
  [DESKTOP]: {
    width: 294,
    height: 58,
    marginLeft: 0
  },
  [PHONE]: {
    width: 345,
    fontSize: 20,
    margin: 'auto'
  },
  '&:focus': {
    border: `1px solid ${CHARCOAL}`
  }
};

const Container = glamorous.div(({background = WHITE}) => ({
  minHeight: 1980,
  background: background,
  marginBottom: 90,
  padding: '0 15px',
  [DESKTOP]: {
    padding: 0,
    margin: 'auto',
    minHeight: 1320,
    marginBottom: 0
  },
  [TABLET]: {
    padding: 0,
    minHeight: 1980
  },
  [PHONE]: {
    padding: 0,
    minHeight: 1930,
    marginBottom: 0
  }
}));

const SubContainer = glamorous.div(
  {
    ...BASE_TEXT,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
    margin: '0 auto',
    padding: 0
  },
  ({customStyle}) => (customStyle ? customStyle : {})
);

const InnerDiv = glamorous.div({
  margin: '69px auto 0px auto',
  width: '100%',
  [LAPTOP]: {
    textAlign: 'center'
  },
  [PHONE]: {
    margin: '55px auto 0'
  }
});

const Heading = glamorous.h1({
  ...BASE_TEXT,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  textAlign: 'center',
  fontSize: 75,
  fontWeight: WEIGHT.ULTRA_BLACK,
  lineHeight: '72px',
  color: CHARCOAL,
  maxWidth: MAX_PAGE_WIDTH,
  margin: '99px auto 28px',
  [LAPTOP]: {
    margin: '69px auto 16px',
    fontSize: 55
  },
  [PHONE]: {
    fontSize: 28,
    lineHeight: '72px',
    margin: '70px auto 10px'
  }
});

const HeadingSubTitle = glamorous.div({
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  textAlign: 'center',
  fontSize: 30,
  fontWeight: WEIGHT.ULTRA_BLACK,
  lineHeight: '42px',
  color: CHARCOAL,
  maxWidth: MAX_PAGE_WIDTH,
  margin: 'auto',
  marginBottom: 15,
  [LAPTOP]: {
    margin: '0 auto',
    marginBottom: 16,
    fontSize: 28,
    width: 707
  },
  [PHONE]: {
    fontSize: 18,
    maxWidth: 280,
    lineHeight: '24px'
  }
});

const SubHeading = glamorous.div({
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  fontWeight: WEIGHT.NORMAL,
  fontSize: 24,
  textAlign: 'center',
  lineHeight: '44px',
  color: BLACK,
  margin: 'auto',
  [DESKTOP]: {
    maxWidth: 930
  },
  [LAPTOP]: {
    margin: '0 auto',
    marginBottom: 16,
    width: 741
  },
  [PHONE]: {
    fontSize: 18,
    lineHeight: '33px',
    maxWidth: 300
  }
});

const ButtonContainer = glamorous.div({
  maxWidth: MAX_PAGE_WIDTH,
  margin: 'auto',
  marginTop: 35,
  marginBottom: 20,
  display: 'flex',
  justifyContent: 'center',
  [LAPTOP]: {
    width: 'fit-content',
    margin: '30px auto 0'
  },
  [PHONE]: {
    width: 'auto',
    flexDirection: 'column'
  }
});

const LearnMore = glamorous(GhostButton)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 191,
  padding: '0 15px',
  height: 50,
  fontSize: 20,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  zIndex: 99,
  marginLeft: 31,
  fontWeight: WEIGHT.ULTRA_BOLD,
  ':focus': {
    background: FOCUS_BLUE,
    color: WHITE
  },
  ':hover': {
    background: FOCUS_BLUE,
    color: WHITE
  },
  [DESKTOP]: {
    height: 58
  },
  [PHONE]: {
    width: 345,
    height: 50,
    marginLeft: 0,
    padding: 0,
    margin: 'auto',
    marginTop: 20
  }
}).withComponent('a');

const SSEnterprise = glamorous.div({
  background: CHARCOAL,
  boxShadow: '0px 4.55944px 41.035px rgba(0, 0, 0, 0.1)',
  borderRadius: 8,
  margin: 'auto',
  width: '100%',
  zIndex: 2,
  [DESKTOP]: {
    minHeight: 446,
    maxHeight: 530,
    marginLeft: 15,
    marginRight: 15
  },
  [LAPTOP]: {
    position: 'relative',
    '&:last-child': {
      marginTop: 280
    }
  },
  [TABLET]: {
    margin: 'auto',
    position: 'relative',
    width: '94%',
    '&:last-child': {
      marginTop: 280
    }
  },
  [PHONE]: {
    width: 'auto',
    minHeight: 434,
    margin: '0 20px',
    position: 'relative',
    '&:last-child': {
      marginTop: 160
    }
  }
});

const Title = glamorous.h3({
  ...BASE_TEXT,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  fontStyle: 'normal',
  fontWeight: WEIGHT.ULTRA_BLACK,
  fontSize: 28,
  lineHeight: '50px',
  color: WHITE,
  margin: '0 0 10px 0',
  [PHONE]: {
    fontSize: 20,
    textAlign: 'left'
  }
});

const Subtitle = glamorous.p({
  ...BASE_TEXT,
  fontFamily: `${FONTS.CEREBRI_SANS_PRO} !important`,
  fontStyle: 'normal',
  fontWeight: WEIGHT.NORMAL,
  fontSize: 20,
  lineHeight: '43px',
  color: WHITE,
  textAlign: 'left',
  margin: '0 0 20px 0',
  [DESKTOP]: {
    marginBottom: 30
  },
  [PHONE]: {
    fontSize: 18,
    lineHeight: '36px'
  },
  [LAPTOP]: {
    fontSize: 18
  }
});

const Button = glamorous(SimpleButton)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 58,
  fontSize: 18,
  fontFamily: FONTS.CEREBRI_SANS_PRO,
  zIndex: 99,
  color: WHITE,
  background: 'transparent',
  border: `1px solid ${WHITE}`,
  fontWeight: WEIGHT.ULTRA_BOLD,
  padding: 15,
  ':focus': {
    background: ALPHA(WHITE, 0.1),
    color: WHITE
  },
  ':hover': {
    background: ALPHA(WHITE, 0.1),
    border: `1px solid ${WHITE}`
  },
  [DESKTOP]: {
    height: 58
  },
  [PHONE]: {
    display: 'inline-flex',
    margin: 15,
    minHeight: 25,
    marginBottom: 30,
    fontSize: 16
  }
}).withComponent('a');

const CardContainer = glamorous.div({
  [TABLET]: {
    position: 'relative',
    marginTop: -135
  },
  [DESKTOP]: {
    display: 'flex',
    position: 'absolute',
    marginTop: -165,
    maxWidth: MAX_PAGE_WIDTH
  },
  [PHONE]: {
    marginLeft: 0,
    display: 'block',
    marginTop: 30,
    position: 'relative'
  }
});

const DataContainer = glamorous.div({
  padding: '81px 40px 79px 66px',
  [PHONE]: {
    padding: '40px 0 0 45px',
    display: 'inline'
  }
});

const TextContainer = glamorous.div({
  textAlign: 'left',
  [PHONE]: {
    paddingLeft: 30,
    paddingRight: 30,
    margin: 'auto',
    marginTop: 20
  }
});

const BackgroundContainer = glamorous.div({
  [DESKTOP]: {
    minHeight: 450,
    maxWidth: MAX_PAGE_WIDTH,
    margin: 'auto'
  },
  [LAPTOP]: {
    marginTop: -132
  },
  [PHONE]: {
    position: 'absolute',
    top: 780,
    marginTop: 0
  }
});

const PatternImage = glamorous.img({
  marginTop: -152,
  position: 'relative',
  top: 0,
  [DESKTOP]: {
    width: '100%',
    height: 660
  },
  [PHONE]: {
    marginTop: -30,
    height: 460
  }
});

const ImageOne = glamorous.img(({customStyle}) => (customStyle ? customStyle : {}));

const ImageTwo = glamorous.img(({customStyle}) => (customStyle ? customStyle : {}));

const cardDetails = [
  {
    title: 'StackShare Enterprise',
    subtitle: (
      <>
        <strong>Our platform</strong> gives companies <strong>real-time visibility</strong> into all
        the tech stacks in use across their engineering teams. StackShare Enterprise helps CTOs and
        other technical leaders make smarter decisions and improve governance
      </>
    ),
    buttonText: 'Explore StackShare Enterprise',
    linkTo: '/enterprise',
    color: CHARCOAL,
    styleOne: {
      position: 'absolute',
      left: -20,
      zIndex: 1,
      [DESKTOP]: {
        width: '52%',
        height: 440,
        bottom: 335
      },
      [LAPTOP]: {
        width: '85%',
        left: '7%',
        top: '31%'
      },
      [TABLET]: {
        width: '94%',
        top: '-18%',
        left: '1%'
      },
      [PHONE]: {
        width: '100%',
        top: -110,
        left: -12
      }
    },
    styleTwo: {
      position: 'absolute',
      zIndex: 3,
      [DESKTOP]: {
        width: '45%',
        bottom: 460,
        height: 250
      },
      [LAPTOP]: {
        width: '66%',
        right: 145,
        top: 610
      },
      [TABLET]: {
        width: '68%',
        left: 110,
        top: -220
      },
      [PHONE]: {
        width: '80%',
        top: -140,
        marginTop: 10,
        left: 20
      }
    },
    image1: 'https://img.stackshare.io/fe/landing/enterprise-one.svg',
    image2: 'https://img.stackshare.io/fe/landing/enterprise-two.svg'
  },
  {
    title: 'StackShare Community',
    subtitle: (
      <>
        <strong>1M developers</strong> share their public stacks, favorite tools, advice, and
        opinions on thousands of technologies. Ask for advice, contribute your expertise, and stay
        current with the latest tech trends.
      </>
    ),
    buttonText: 'Browse the StackShare Community',
    linkTo: '/feed',
    color: DODGER_BLUE,
    styleOne: {
      position: 'absolute',
      bottom: 327,
      zIndex: 1,
      right: 25,
      [DESKTOP]: {
        width: '50%',
        right: 0,
        bottom: 400
      },
      [LAPTOP]: {
        bottom: 336,
        right: 148
      },
      [TABLET]: {
        width: '78%',
        bottom: 340,
        right: 108
      },
      [PHONE]: {
        width: '100%',
        bottom: 365,
        right: 0
      }
    },
    styleTwo: {
      position: 'absolute',
      bottom: 384,
      right: 0,
      zIndex: 3,
      [DESKTOP]: {
        width: '44%',
        right: -10,
        bottom: 440
      },
      [LAPTOP]: {
        bottom: 375,
        right: 78
      },
      [TABLET]: {
        width: '70%',
        right: 75,
        bottom: 383
      },
      [PHONE]: {
        width: '88%',
        bottom: 396,
        right: 0
      }
    },
    image1: 'https://img.stackshare.io/fe/landing/community_one.svg',
    image2: 'https://img.stackshare.io/fe/landing/community-two.png'
  }
];

const patternImage = 'https://img.stackshare.io/fe/landing/pattern.svg';

const HeadingComponent = () => {
  return (
    <Container>
      <SubContainer>
        <InnerDiv>
          <Heading>Tech Stack Intelligence</Heading>
          <HeadingSubTitle>
            Beat tech sprawl and make data-driven technology decisions
          </HeadingSubTitle>
          <SubHeading>
            Tech Stack Intelligence provides real-time visibility into all the developer tools,
            services, and packages you&apos;re using internally, down to the version level, across
            all your Git repos.
          </SubHeading>

          <ButtonContainer>
            <RequestDemo
              eventProperties={{
                index: 1,
                sectionName: 'SignupHeader'
              }}
              extraStyle={RequestDemoExtraStyle}
            />
            <LearnMore href={`/enterprise`} color={FOCUS_BLUE}>
              Learn More
            </LearnMore>
          </ButtonContainer>
          <PatternImage src={patternImage} />
          <div style={{background: 'rgba(5,143,250, 0.03)'}}>
            <BackgroundContainer>
              <CardContainer>
                {cardDetails.map((item, index) => (
                  <>
                    <ImageOne src={item.image1} customStyle={item.styleOne} />
                    <ImageTwo src={item.image2} customStyle={item.styleTwo} />
                    <SSEnterprise style={{backgroundColor: item.color}} key={index}>
                      <DataContainer>
                        <TextContainer>
                          <Title>{item.title}</Title>
                          <Subtitle>{item.subtitle}</Subtitle>
                        </TextContainer>
                        <Button href={item.linkTo}>{item.buttonText}</Button>
                      </DataContainer>
                    </SSEnterprise>
                  </>
                ))}
              </CardContainer>
            </BackgroundContainer>
          </div>
        </InnerDiv>
      </SubContainer>
    </Container>
  );
};

export default HeadingComponent;
